import { Clear, Search } from "@mui/icons-material";
import {
	Autocomplete,
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { format, startOfMonth, endOfMonth, subDays, subMonths, subYears, addMonths, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useDebounce } from "../../../shared/hooks";
import { clientService } from "../../../shared/services/api/client/clientService";

export type FireClientOption = {
	id: string;
	label: string;
};

interface ComissionToolbarProps {
	textSearch?: string;
	onChangeTextSearch?: (value: string) => void;
	onClearTextSearch?: () => void;
	showInputSearch?: boolean;

	showDateRange?: boolean;
	onChangeDateStart?: (value: string) => void;
	onChangeDateEnd?: (value: string) => void;

	showStatusSelect?: boolean;
	onChangeStatus?: (value: string) => void;

	showRefreshButton?: boolean;
	onClickRefresh?: () => void;

	showGenerateInvoiceButton?: boolean;
	onClickGenerateInvoice?: () => void;

	showClientSelect?: boolean;
	onChangeClient?: (clientId: string | null) => void;
}

export function ComissionToolbar({
	textSearch = "",
	onChangeTextSearch,
	onClearTextSearch,
	showInputSearch = true,
	showDateRange = true,
	onChangeDateStart,
	onChangeDateEnd,
	showStatusSelect = false,
	onChangeStatus,
	showRefreshButton = true,
	onClickRefresh,
	showGenerateInvoiceButton = false,
	onClickGenerateInvoice,
	showClientSelect = false,
	onChangeClient,
}: ComissionToolbarProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const today = new Date();

	const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
	const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
	const [status, setStatus] = useState("todos");
	const [periodType, setPeriodType] = useState("1_mes");

	const { debounce } = useDebounce();
	const [clientInput, setClientInput] = useState("");
	const [clientSelected, setClientSelected] = useState<FireClientOption | null>(null);
	const [clientLoading, setClientLoading] = useState(false);
	const [clientOptions, setClientOptions] = useState<FireClientOption[]>([]);

	const getDateRangeFromPeriod = (period: string): [string, string] => {
		const now = new Date();
		let start: Date = now;
		let end: Date = now;

		switch (period) {
			case "15_dias":
				start = subDays(now, 15);
				end = now;
				break;
			case "1_mes":
				start = startOfMonth(now);
				end = endOfMonth(now);
				break;
			case "2_meses":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(now);
				break;
			case "3_meses":
				start = startOfMonth(subMonths(now, 2));
				end = endOfMonth(now);
				break;
			case "1_ano":
				start = startOfMonth(subYears(now, 1));
				end = endOfMonth(now);
				break;
			case "mes_anterior":
				// Para o mês anterior, usamos subMonths para voltar 1 mês
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(subMonths(now, 1));
				break;
			case "proximo_mes":
				// Para o próximo mês, usamos addMonths para avançar 1 mês
				start = startOfMonth(addMonths(now, 1));
				end = endOfMonth(addMonths(now, 1));
				break;
		}

		return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
	};

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setPeriodType(value);

		if (value !== "personalizado") {
			const [newStart, newEnd] = getDateRangeFromPeriod(value);
			setStartDate(newStart);
			setEndDate(newEnd);
			onChangeDateStart?.(newStart);
			onChangeDateEnd?.(newEnd);
		}
	};

	const handleStatusChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setStatus(value);
		onChangeStatus?.(value);
	};

	useEffect(() => {
		if (!showClientSelect) return;
		if (!clientInput) {
			setClientOptions([]);
			return;
		}

		setClientLoading(true);

		debounce(() => {
			clientService
				.getSelect(1, clientInput.trim())
				.then((result: any) => {
					if (result instanceof Error) return;

					const options = result?.data?.map((c: any) => ({
						id: c.id,
						label: c.name,
					}));

					setClientOptions(options ?? []);
				})
				.finally(() => setClientLoading(false));
		});
	}, [clientInput]);

	const handleSelectClient = (value: FireClientOption | null) => {
		setClientSelected(value);
		onChangeClient?.(value?.id ?? null);
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems="center">
				{showDateRange && (
					<>
						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel id="period-label">Período</InputLabel>
								<Select labelId="period-label" value={periodType} label="Período" onChange={handlePeriodChange}>
									<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
									<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
									<MenuItem value="1_mes">Mês Atual</MenuItem>
									<MenuItem value="proximo_mes">Próximo Mês</MenuItem> {/* Adicionando a opção "Próximo Mês" */}
									<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
									<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
									<MenuItem value="1_ano">Último ano</MenuItem>
									<MenuItem value="personalizado">Personalizado</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{periodType === "personalizado" && (
							<>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Inicial"
										value={startDate}
										onChange={(e) => {
											setStartDate(e.target.value);
											onChangeDateStart?.(e.target.value);
										}}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Final"
										value={endDate}
										onChange={(e) => {
											setEndDate(e.target.value);
											onChangeDateEnd?.(e.target.value);
										}}
									/>
								</Grid>
							</>
						)}
					</>
				)}

				{showClientSelect && (
					<Grid item xs={12} sm={6} md={3}>
						<Autocomplete
							fullWidth
							size="small"
							loading={clientLoading}
							options={clientOptions}
							value={clientSelected}
							onChange={(_, v) => handleSelectClient(v)}
							onInputChange={(_, value) => setClientInput(value)}
							renderInput={(params) => <TextField {...params} label="Imobiliária" />}
						/>
					</Grid>
				)}

				{showStatusSelect && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="status-label">Status</InputLabel>
							<Select labelId="status-label" value={status} label="Status" onChange={handleStatusChange}>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="preview">Prévia</MenuItem>
								<MenuItem value="open">Aberto</MenuItem>
								<MenuItem value="suspended">Suspenso</MenuItem>
								<MenuItem value="canceled">Cancelado</MenuItem>
								<MenuItem value="paid">Pago</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{showInputSearch && (
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							size="small"
							placeholder="Pesquisar..."
							value={textSearch}
							onChange={(e) => onChangeTextSearch?.(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => {
												onClearTextSearch?.();
												onChangeTextSearch?.("");
											}}
										>
											<Clear />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				)}
				<Grid item xs={12} sm={12} md>
					<Box display="flex" justifyContent="space-between" gap={1}>
						{showRefreshButton && (
							<Button variant="contained" onClick={onClickRefresh}>
								Pesquisar
							</Button>
						)}

						{showGenerateInvoiceButton && (
							<Button variant="contained" color="secondary" onClick={onClickGenerateInvoice}>
								Faturar selecionados
							</Button>
						)}
					</Box>
				</Grid>
			</Grid>

			{showDateRange && (
				<Typography variant="body2" sx={{ mt: 1, ml: 1, fontStyle: "italic", color: "text.secondary" }}>
					Período: {format(parseISO(startDate), "dd/MM/yyyy")} - {format(parseISO(endDate), "dd/MM/yyyy")}
				</Typography>
			)}
		</Box>
	);
}
